import { t } from '../core/i18n';

export interface RouteOfferData {
  reason: 'traffic' | 'fast-roads';
  routeName: string;
  /** Positive means the offered route is faster. */
  savesMinutes: number;
  /** Mapbox live-vs-typical delay on the route currently being driven. */
  currentDelayMinutes: number;
  /** Positive means the offered route is longer. */
  distanceDeltaKm: number;
  trafficDelaySavedMinutes: number;
  congestionDropPoints: number;
  fastRoadGainMinutes: number;
  motorwayGainMinutes: number;
  turnsSaved: number;
}

function oneDecimal(n: number): string {
  return (Math.round(Math.abs(n) * 10) / 10).toFixed(Math.abs(n) < 10 ? 1 : 0);
}

export class RouteOffer {
  readonly el: HTMLElement;
  private accept: (() => void) | null = null;
  private decline: (() => void) | null = null;
  private hideCb: (() => void) | null = null;

  constructor(parent: HTMLElement) {
    this.el = document.createElement('div');
    this.el.className = 'route-offer';
    this.el.classList.toggle('route-offer--car', parent.closest('.app--car') !== null);
    this.el.hidden = true;
    this.el.setAttribute('role', 'dialog');
    this.el.setAttribute('aria-modal', 'true');
    document.body.appendChild(this.el);

    this.el.addEventListener('click', (event) => {
      const action = (event.target as HTMLElement).closest<HTMLElement>('[data-route-offer]')?.dataset
        .routeOffer;
      if (action === 'accept') {
        const callback = this.accept;
        this.hide();
        callback?.();
      } else if (action === 'decline') {
        this.dismiss();
      }
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.el.hidden) {
        event.preventDefault();
        this.dismiss();
      }
    });
  }

  show(data: RouteOfferData, actions: { onAccept: () => void; onDecline: () => void; onHide?: () => void }): void {
    this.accept = actions.onAccept;
    this.decline = actions.onDecline;
    this.hideCb = actions.onHide ?? null;
    const metrics: string[] = [];
    const saves = Math.round(data.savesMinutes);
    if (data.reason === 'traffic') {
      metrics.push(
        this.metric(
          `${Math.max(1, Math.round(data.currentDelayMinutes))} min`,
          t('routeOffer.currentDelay'),
          'neutral',
        ),
        this.metric(
          `${Math.max(1, saves)} min`,
          t('routeOffer.alternativeSaves'),
          'good',
        ),
      );
    } else {
      metrics.push(
        this.metric(
          saves >= 0 ? `${Math.max(1, saves)}` : `${Math.max(1, Math.abs(saves))}`,
          saves >= 0 ? t('routeOffer.faster') : t('routeOffer.slower'),
          saves >= 0 ? 'good' : 'neutral',
        ),
      );
    }
    metrics.push(
      this.metric(
        `${oneDecimal(data.distanceDeltaKm)} km`,
        data.distanceDeltaKm >= 0 ? t('routeOffer.longer') : t('routeOffer.shorter'),
        'neutral',
      ),
    );
    if (data.reason !== 'traffic' && data.trafficDelaySavedMinutes >= 1) {
      metrics.push(
        this.metric(
          `${Math.round(data.trafficDelaySavedMinutes)} min`,
          t('routeOffer.lessDelay'),
          'good',
        ),
      );
    } else if (data.reason !== 'traffic' && data.congestionDropPoints >= 2) {
      metrics.push(
        this.metric(
          `${Math.round(data.congestionDropPoints)}%`,
          t('routeOffer.lessTraffic'),
          'good',
        ),
      );
    }
    if (data.fastRoadGainMinutes >= 1) {
      metrics.push(
        this.metric(
          `+${Math.round(data.fastRoadGainMinutes)} min`,
          t('routeOffer.fastRoad'),
          'good',
        ),
      );
    }
    if (data.motorwayGainMinutes >= 2) {
      metrics.push(
        this.metric(
          `+${Math.round(data.motorwayGainMinutes)} min`,
          t('routeOffer.motorway'),
          'good',
        ),
      );
    }
    if (Math.abs(data.turnsSaved) >= 2) {
      metrics.push(
        this.metric(
          `${Math.abs(data.turnsSaved)}`,
          data.turnsSaved > 0 ? t('routeOffer.fewerTurns') : t('routeOffer.moreTurns'),
          data.turnsSaved > 0 ? 'good' : 'neutral',
        ),
      );
    }

    const title = data.reason === 'traffic' ? t('routeOffer.trafficTitle') : t('routeOffer.fastTitle');
    this.el.setAttribute('aria-labelledby', 'route-offer-title');
    const trafficSource =
      data.reason === 'traffic'
        ? `<div class="route-offer__source">${t('routeOffer.liveTrafficEstimate')}</div>`
        : '';
    this.el.innerHTML = `
      <div class="route-offer__card">
        <button class="route-offer__close" data-route-offer="decline" aria-label="${t('routeOffer.close')}">×</button>
        <div class="route-offer__head">
          <span class="route-offer__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24"><path d="M5 19 C5 13 9 11 12 9 C15 7 17 5 17 2 M13 5 L17 1 L21 5 M5 10 C7 10 8.5 11 10 13" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </span>
          <span class="route-offer__heading">
            <strong id="route-offer-title">${title}</strong>
            <em>${t('routeOffer.why')}</em>
          </span>
        </div>
        ${data.routeName ? `<div class="route-offer__route">${escapeHtml(data.routeName)}</div>` : ''}
        <div class="route-offer__metrics">${metrics.join('')}</div>
        ${trafficSource}
        <div class="route-offer__actions">
          <button class="route-offer__decline" data-route-offer="decline">${t('routeOffer.decline')}</button>
          <button class="route-offer__accept" data-route-offer="accept">${t('routeOffer.accept')}</button>
        </div>
      </div>`;
    this.el.hidden = false;
    this.el.querySelector<HTMLButtonElement>('.route-offer__decline')?.focus();
  }

  hide(): void {
    this.el.hidden = true;
    const cb = this.hideCb;
    this.accept = null;
    this.decline = null;
    this.hideCb = null;
    cb?.();
  }

  private metric(value: string, label: string, tone: 'good' | 'neutral'): string {
    return `<div class="route-offer__metric route-offer__metric--${tone}"><strong>${value}</strong><span>${label}</span></div>`;
  }

  private dismiss(): void {
    const callback = this.decline;
    this.hide();
    callback?.();
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch]!);
}
